import React, { useEffect, useId, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';

/**
 * « Le secret des parents » — ce que la boîte ne dit pas.
 *
 * Décrit par le créateur du jeu le 08/10/2026 : pour répondre, les équipes
 * font un maximum de bruit avec des jouets d'enfants, et ce sont les parents
 * qui décident quelle équipe a la parole. Comme ils connaissent les questions
 * à l'avance, ils peuvent orienter la partie en douceur — jusqu'à faire
 * gratter la dernière pièce à la personne qu'ils veulent demander comme
 * parrain ou marraine.
 *
 * `masque` : la page des règles est aussi la cible du QR code de la boîte,
 * qu'un invité peut scanner pendant la partie. Le secret y reste replié
 * derrière un bouton « parents uniquement », comme la réponse de la question
 * subsidiaire.
 */
export default function SecretDesParents({ masque = false, className = '' }) {
  const { t } = useLang();
  const idTitre = useId();
  const texteRef = useRef(null);
  const [ouvert, setOuvert] = useState(!masque);
  const depliee = useRef(false);

  // Le bouton disparaît au clic : on pose le focus sur le texte révélé pour
  // qu'un lecteur d'écran ne se retrouve pas en haut de la page. Un effet
  // plutôt qu'un requestAnimationFrame, suspendu dans un onglet en arrière-
  // plan ; et seulement après un vrai dépliage, jamais au chargement.
  useEffect(() => {
    if (ouvert && depliee.current) texteRef.current?.focus();
  }, [ouvert]);

  const reveler = () => {
    depliee.current = true;
    setOuvert(true);
  };

  return (
    <aside
      aria-labelledby={idTitre}
      className={`rounded-2xl border-2 border-dashed border-[#EDB82F] bg-[#FFF8E6] p-5 text-left sm:p-7 ${className}`}
    >
      <p className="m-0 mb-1 text-xs font-bold uppercase tracking-[0.15em] text-[#8A5A00]">
        {t('secret.label')}
      </p>
      <h3 id={idTitre} className="m-0 mb-3 text-lg font-black text-[#33403B] sm:text-xl">
        {t('secret.titre')}
      </h3>
      {ouvert ? (
        <>
          <p
            ref={texteRef}
            tabIndex={-1}
            className="m-0 mb-3 text-[15px] leading-relaxed text-[#33403B] focus:outline-none"
          >
            {t('secret.texte')}
          </p>
          <p className="m-0 mb-4 text-[15px] leading-relaxed text-[#33403B]">{t('secret.parrain')}</p>
          <p className="m-0 text-[17px] font-black italic leading-snug text-[#17453E]">
            {t('secret.chute')}
          </p>
        </>
      ) : (
        <button
          type="button"
          onClick={reveler}
          data-cta="regles:voir-secret"
          className="rounded-lg bg-gradient-to-br from-[#4AA294] to-[#2F7E72] px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-[#EDB82F]"
        >
          {t('secret.afficher')}
        </button>
      )}
    </aside>
  );
}
